import translate, { formatNumber } from '@/services/translate';
import { formatter } from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';

function isPresentNumber(value) {
  return typeof value === 'number' && !Number.isNaN(value);
}

function isPresentText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function buildTextItem(id, labelKey, value) {
  if (isPresentText(value)) return { id, labelKey, text: value };
  if (isPresentNumber(value)) return { id, labelKey, text: String(value) };
  return { id, labelKey };
}

function buildNumberItem(id, labelKey, value) {
  if (!isPresentNumber(value)) return { id, labelKey };
  const text = formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return { id, labelKey, text };
}

function buildDateItem(id, labelKey, value) {
  // Data sem hora vem como meia-noite UTC; no fuso do usuário cairia no dia anterior.
  const text = formatter(value, formats.DATE_SHORT, timezones.UTC);
  if (!text) return { id, labelKey };
  return { id, labelKey, text };
}

function buildEnumItem(id, labelKey, enumNamespace, value) {
  if (!isPresentText(value)) return { id, labelKey };
  return { id, labelKey, valueKey: `enums.${enumNamespace}.${value}` };
}

function buildBooleanItem(id, labelKey, value) {
  if (typeof value !== 'boolean') return { id, labelKey };
  return { id, labelKey, boolean: value };
}

// aplicacaoTransformador chega como 'potency'/'distribution'/'oven' (ver
// transformerApplicationMap em sigma-mappings), mas as chaves de tradução em
// enums.transformer-application usam o prefixo "transformer-" (mesmo padrao
// de Form.vue, generalData)
function buildTransformerApplicationItem(value) {
  const id = 'transformer-application';
  const labelKey = 'cma.asset-info.transformer-application';
  if (!isPresentText(value)) return { id, labelKey };
  return { id, labelKey, valueKey: `enums.transformer-application.transformer-${value}` };
}

export function buildAssetInfoItems({
  transformerData, registrationData, coolingStagesData,
} = {}) {
  const transformer = transformerData || {};
  const registration = registrationData || {};
  const coolingStages = coolingStagesData || {};

  return [
    {
      id: 'type',
      labelKey: 'cma.asset-info.type',
      ...(transformer.type ? { valueKey: `enums.transformer-type.${transformer.type}` } : {}),
    },
    {
      id: 'manufacturing-year',
      labelKey: 'asset-information.asset-info.manufacturing-year',
      ...(isPresentNumber(registration.year) ? { text: String(registration.year) } : {}),
    },
    buildTextItem('identification', 'cma.asset-info.identification', registration.identification),
    buildTextItem('serial-number', 'cma.asset-info.serial-number', registration.serialNumber),
    buildTextItem('company', 'header.company', registration.companyName),
    buildTextItem('installation', 'header.installation', registration.installationName),
    buildDateItem('operation-entry', 'asset-information.asset-info.operation-entry', registration.operationEntry),
    buildTextItem('description', 'asset-information.asset-info.asset-description', registration.description),
    buildTransformerApplicationItem(transformer.transformerApplication),
    buildBooleanItem('tertiary-winding', 'cma.asset-info.tertiary-winding', transformer.tertiaryWinding),
    buildTextItem(
      'monitored-winding-amount',
      'cma.asset-info.monitored-winding-amount',
      transformer.monitoredWindingAmount,
    ),
    buildBooleanItem(
      'communicating-oil-oltc',
      'cma.asset-info.communicating-oil-oltc',
      transformer.communicatingOilOltcTransformer,
    ),
    buildTextItem(
      'cooling-stages-amount',
      'cma.asset-info.cooling-stages-amount',
      coolingStages.coolingStagesAmount,
    ),
    buildNumberItem('oil-volume', 'fields.asset.transformer.oil-volume', transformer.oilVolume),
    buildNumberItem('oil-mass', 'fields.asset.transformer.oil-mass', transformer.oilKiloMass),
    buildNumberItem(
      'tank-mass-and-accessories',
      'fields.asset.transformer.tank-mass-and-accessories',
      transformer.reservoirMass,
    ),
    buildNumberItem(
      'mass-active-part',
      'fields.asset.transformer.mass-active-part',
      transformer.activeSideMass,
    ),
    buildNumberItem('total-weight', 'fields.asset.transformer.total-weight', transformer.totalWeight),
    buildEnumItem(
      'type-of-insulating-paper',
      'fields.asset.transformer.type-of-insulating-paper',
      'insulating-paper-type',
      transformer.insulationType,
    ),
    buildEnumItem('oil-type', 'fields.asset.transformer.oil-type', 'oil-type', transformer.oilType),
  ];
}

function formatMeasure(value, unit) {
  if (!isPresentNumber(value)) return null;
  return `${formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${unit}`;
}

// Um item por estagio (rotulo "Estagio N", valor com tipo/potencia/gradiente
// numa unica linha) em vez de tabela: o painel e estreito (260px) e uma
// tabela de 4 colunas so cabe com scroll horizontal, que a tela nao deve ter.
export function buildCoolingStageItems(coolingStagesData) {
  const stages = (coolingStagesData && coolingStagesData.coolingStages) || [];
  return stages.map(stage => ({
    id: `stage-${stage.stageNumber}`,
    label: translate('asset-information.cooling-stages.stage-number', { number: stage.stageNumber }),
    text: [
      stage.coolingType,
      formatMeasure(stage.nominalPotency, 'MVA'),
      formatMeasure(stage.oilEnvironmentTopGradient, '°C'),
    ].filter(Boolean).join(' · '),
  }));
}
